import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import AppErrorBoundary from "@/components/common/AppErrorBoundary";
import { useAuthStore } from "@/store/authStore";

import Login from "@/pages/auth/Login";
import AuthCallback from "@/pages/auth/AuthCallback";
import GoogleCallback from "@/pages/auth/GoogleCallback";
import FitTipsLibrary from "@/pages/community/FitTipsLibrary";
import CommunityRules from "@/pages/community/CommunityRules";
import ArenaJoin from "@/pages/arena/ArenaJoin";
import ArenaGymPublic from "@/pages/arena/ArenaGymPublic";
import ArenaCheckin from "@/pages/arena/ArenaCheckin";
import ArenaFind from "@/pages/arena/ArenaFind";
import ArenaPlans from "@/pages/arena/ArenaPlans";
import ArenaBoardTV from "@/pages/arena/ArenaBoardTV";
import ArenaJourney from "@/pages/arena/ArenaJourney";
import MemberJourney from "@/pages/journey/MemberJourney";
import DayView from "@/pages/journey/DayView";
import ArenaDashboard from "@/pages/arena/ArenaDashboard";
import Onboarding from "@/pages/auth/Onboarding";
import ProfileSetup from "@/pages/auth/ProfileSetup";
import Home from "@/pages/home/Home";
import HomeWorkoutPage from "@/pages/workout/HomeWorkout";
import LiveSession from "@/pages/workout/LiveSession";
import PlanPreview from "@/pages/workout/PlanPreview";
import ComingSoon from "@/pages/workout/ComingSoon";
import MuscleGroup from "@/pages/workout/MuscleGroup";
import ExerciseDetail from "@/pages/workout/ExerciseDetail";
import Workouts from "@/pages/workout/Workouts";
import ProfessionalOnboarding from "@/pages/professional/ProfessionalOnboarding";
import { Submitted } from "@/pages/professional/ProTerminals";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import ProGate from "@/components/common/ProGate";
import FoodScanner from "@/pages/nutrition/FoodScanner";
import AIChat from "@/pages/nutrition/AIChat";
import Nutrition from "@/pages/nutrition/Nutrition";
import SavedProducts from "@/pages/nutrition/SavedProducts";
import HealthDashboard from "@/pages/health/HealthDashboard";
import { WorkoutHistoryPage } from "@/components/workout/WorkoutHistory";
import BodyScan from "@/pages/health/BodyScan";
import TrainerPortal from "@/pages/professional/TrainerPortal";
import Inbox from "@/pages/inbox/Inbox";
import AdminBroadcast from "@/pages/admin/AdminBroadcast";
import AdminBrandApplications from "@/pages/admin/AdminBrandApplications";
import SuperadminApp from "@/pages/superadmin/SuperadminApp";
import SuperadminCommandCenter from "@/pages/superadmin/CommandCenter";
import SuperadminPlaceholder from "@/pages/superadmin/SuperadminPlaceholder";
import SuperadminUsers from "@/pages/superadmin/Users";
import SuperadminAlerts from "@/pages/superadmin/Alerts";
import { ActiveUsers, NewUsers, GuestSessions, Retention } from "@/pages/superadmin/UserSegments";
import Professionals, { PendingProfessionals, AITrainerTasks } from "@/pages/superadmin/Professionals";
import {
  WorkoutManager, ExerciseDatabase, FoodDatabase, ProgrammeManager, CommunityModeration, ContentReports,
} from "@/pages/superadmin/Content";
import {
  MarketplaceProducts, MarketplaceOrders, MarketplaceBrands, MarketplaceReports,
} from "@/pages/superadmin/Marketplace";
import {
  RevenueOverview, RevenueTransactions, RevenuePayouts, RevenueMRR,
} from "@/pages/superadmin/Revenue";
import {
  PlatformHealth, PlatformCosts, PlatformErrors, NotificationLog, ScannerDiagnostics,
} from "@/pages/superadmin/Platform";
import { BroadcastCompose, BroadcastLog } from "@/pages/superadmin/Broadcast";
import {
  FeatureFlags, CostThresholds, AdminAccounts, AuditLog, ConfigDiagnostics,
} from "@/pages/superadmin/Settings";
import {
  ArenaApplications, ArenaGyms, ArenaPasses, ArenaCheckinsAdmin, ArenaDisputes, ArenaRevenue,
} from "@/pages/superadmin/Arena";
import AICoach from "@/pages/ai-trainer/AICoach";
import {
  AudioCoachSettings,
  NotificationsSettings,
  ConnectedDevicesSettings,
  PrivacySettings,
} from "@/pages/settings/SubSettings";
import TrainerBrowse from "@/pages/trainers/TrainerBrowse";
import TrainerProfile from "@/pages/trainers/TrainerProfile";
import CommunityHub from "@/pages/Community";
import Pricing from "@/pages/subscription/Pricing";
import Settings from "@/pages/settings/Settings";
import DataStorage from "@/pages/settings/DataStorage";
import NotificationsInbox from "@/pages/notifications/NotificationsInbox";
import ProtectedRoute from "@/components/common/ProtectedRoute";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import TermsOfService from "@/pages/legal/TermsOfService";
import RefundPolicy from "@/pages/legal/RefundPolicy";
import Marketplace, { MarketplaceSearch } from "@/pages/marketplace/Marketplace";
import ProductDetail from "@/pages/marketplace/ProductDetail";
import { Cart, Checkout, OrderConfirmed } from "@/pages/marketplace/CartCheckout";
import { Sell, MyOrders, MyListings } from "@/pages/marketplace/SellAndLists";
import OrderDetail from "@/pages/marketplace/OrderDetail";
import BrandApply from "@/pages/marketplace/BrandApply";
import MyPlans from "@/pages/profile/MyPlans";
import SquadDetail from "@/pages/community/SquadDetail";
import LiveJoin from "@/pages/live/LiveJoin";
import DeleteAccountPublic from "@/pages/legal/DeleteAccountPublic";

function RootRoute() {
  const user = useAuthStore((s) => s.user);
  if (user) {
    if (user.is_superadmin) return <Navigate to="/superadmin" replace />;
    if (user.role === "admin") return <Navigate to="/admin/professionals" replace />;
    return <Navigate to="/home" replace />;
  }
  // Iter 50: scanner IS the landing page. No slider, no login wall.
  return <FoodScanner />;
}

function NutritionRoute() {
  // Same component on / and /nutrition for logged-out visitors; the
  // members dashboard shows only when authenticated.
  const user = useAuthStore((s) => s.user);
  if (user) return <Nutrition />;
  return <FoodScanner />;
}

function HashSessionDetector({ children }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return children;
}

function App() {
  const checkAuth = useAuthStore((s) => s.checkAuth);
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    checkAuth();
  }, [checkAuth]);

  // Foreground activity ping — one hit per minute while the tab is visible.
  // Backend accepts 3/min so a stray retry can't get us throttled. We skip
  // the ping when the tab is hidden so a background tab doesn't inflate DAU.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const { apiClient } = require("./services/api");
    const ping = () => {
      if (cancelled) return;
      if (document.visibilityState !== "visible") return;
      apiClient.post("/session/ping").catch(() => { /* rate-limit or offline: fine */ });
    };
    ping(); // fire once immediately on mount / login
    const id = setInterval(ping, 60_000);
    const onVis = () => document.visibilityState === "visible" && ping();
    document.addEventListener("visibilitychange", onVis);
    return () => { cancelled = true; clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [user]);

  return (
    <div className="App">
      <AppErrorBoundary>
        <BrowserRouter>
        <HashSessionDetector>
          <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/delete-account" element={<DeleteAccountPublic />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/google/callback" element={<GoogleCallback />} />
            <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
            <Route path="/onboarding/follow-trainers" element={<Navigate to="/home" replace />} />
            <Route path="/onboarding/find-gym" element={<Navigate to="/home" replace />} />
            <Route path="/profile-setup" element={<ProtectedRoute><ProfileSetup /></ProtectedRoute>} />
            <Route path="/arena/join" element={<ArenaJoin />} />
            <Route path="/for-gyms" element={<Navigate to="/arena/join" replace />} />
            <Route path="/list-your-gym" element={<Navigate to="/arena/join" replace />} />
            <Route path="/arena/find" element={<ArenaFind />} />
            <Route path="/arena/plans" element={<ProtectedRoute><ArenaPlans /></ProtectedRoute>} />
            <Route path="/arena/dashboard" element={<ProtectedRoute><ArenaDashboard /></ProtectedRoute>} />
            <Route path="/arena/:code/board" element={<ArenaBoardTV />} />
            <Route path="/arena/:code" element={<ArenaGymPublic />} />
            <Route path="/journey" element={<ProtectedRoute><ArenaJourney /></ProtectedRoute>} />
            <Route path="/me/journey" element={<ProtectedRoute><MemberJourney /></ProtectedRoute>} />
            <Route path="/me/day/:date" element={<ProtectedRoute><DayView /></ProtectedRoute>} />
            <Route path="/checkin/:gym_id/:token" element={<ProtectedRoute><ArenaCheckin /></ProtectedRoute>} />

            <Route path="/community/squad/:squad_id" element={<ProtectedRoute><SquadDetail /></ProtectedRoute>} />
            <Route path="/live/:live_session_id" element={<ProtectedRoute><LiveJoin /></ProtectedRoute>} />

            <Route path="/home" element={<ProtectedRoute><ProGate><Home /></ProGate></ProtectedRoute>} />

            <Route path="/workouts" element={<ProtectedRoute><Workouts /></ProtectedRoute>} />
            <Route path="/workouts/gym" element={<Navigate to="/workouts" replace />} />
            <Route path="/workouts/home" element={<Navigate to="/workouts" replace />} />
            <Route path="/workouts/session/:planId" element={<ProtectedRoute><LiveSession /></ProtectedRoute>} />
            <Route path="/workouts/plan/:planId" element={<ProtectedRoute><PlanPreview /></ProtectedRoute>} />
            <Route path="/workouts/muscle/:groupId" element={<MuscleGroup />} />
            <Route path="/workouts/exercise/:exerciseId" element={<ExerciseDetail />} />
            <Route path="/workouts/coming-soon/yoga" element={<ComingSoon title="Yoga is coming soon" sub="Daily structured yoga flows for strength, mobility, and recovery — built with Indian yoga teachers." image="https://images.unsplash.com/photo-1588286840104-8957b019727f?w=1600&q=85&auto=format" />} />
            <Route path="/workouts/coming-soon/zumba" element={<ComingSoon title="Zumba is coming soon" sub="High-energy dance fitness routines from certified ZIN trainers across India." image="https://images.unsplash.com/photo-1571019613576-2b22c76fd955?w=1600&q=85&auto=format" />} />

            <Route path="/nutrition" element={<NutritionRoute />} />
            <Route path="/nutrition/scanner" element={<FoodScanner />} />
            <Route path="/nutrition/saved" element={<SavedProducts />} />
            <Route path="/nutrition/chat" element={<AIChat />} />

            <Route path="/health" element={<ProtectedRoute><HealthDashboard /></ProtectedRoute>} />
            <Route path="/health/workout-history" element={<ProtectedRoute><WorkoutHistoryPage /></ProtectedRoute>} />
            <Route path="/health/body-scan" element={<ProtectedRoute><BodyScan /></ProtectedRoute>} />
            <Route path="/pro/portal" element={<ProtectedRoute><TrainerPortal /></ProtectedRoute>} />
            <Route path="/inbox" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
            <Route path="/ai-trainer/coach" element={<ProtectedRoute><AICoach /></ProtectedRoute>} />
            <Route path="/admin/broadcast" element={<ProtectedRoute><AdminBroadcast /></ProtectedRoute>} />
            <Route path="/settings/audio" element={<ProtectedRoute><AudioCoachSettings /></ProtectedRoute>} />
            <Route path="/settings/notifications" element={<ProtectedRoute><NotificationsSettings /></ProtectedRoute>} />
            <Route path="/settings/devices" element={<ProtectedRoute><ConnectedDevicesSettings /></ProtectedRoute>} />
            <Route path="/settings/privacy" element={<ProtectedRoute><PrivacySettings /></ProtectedRoute>} />
            <Route path="/settings/data" element={<ProtectedRoute><DataStorage /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><NotificationsInbox /></ProtectedRoute>} />

            <Route path="/trainers" element={<TrainerBrowse />} />
            <Route path="/trainers/:id" element={<TrainerProfile />} />

            <Route path="/community" element={<ProtectedRoute><CommunityHub /></ProtectedRoute>} />
            <Route path="/community/tips" element={<ProtectedRoute><FitTipsLibrary /></ProtectedRoute>} />
            <Route path="/community/rules" element={<CommunityRules />} />

            <Route path="/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />

            <Route path="/pro/apply" element={<ProfessionalOnboarding />} />
            <Route path="/pro/submitted" element={<Submitted />} />

            <Route path="/admin" element={<Navigate to="/admin/professionals" replace />} />
            <Route path="/admin/professionals" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/brand-applications" element={<ProtectedRoute><AdminBrandApplications /></ProtectedRoute>} />

            {/* Super Admin — full multi-session shell. Old /admin route now redirects here. */}
            <Route path="/superadmin" element={<ProtectedRoute><SuperadminApp /></ProtectedRoute>}>
              <Route index element={<SuperadminCommandCenter />} />
              <Route path="alerts" element={<SuperadminAlerts />} />
              <Route path="users" element={<SuperadminUsers />} />
              <Route path="users/active" element={<ActiveUsers />} />
              <Route path="users/new" element={<NewUsers />} />
              <Route path="users/guests" element={<GuestSessions />} />
              <Route path="users/retention" element={<Retention />} />
              <Route path="revenue" element={<RevenueOverview />} />
              <Route path="revenue/transactions" element={<RevenueTransactions />} />
              <Route path="revenue/payouts" element={<RevenuePayouts />} />
              <Route path="revenue/mrr" element={<RevenueMRR />} />
              <Route path="professionals" element={<Professionals />} />
              <Route path="professionals/pending" element={<PendingProfessionals />} />
              <Route path="professionals/ai-tasks" element={<AITrainerTasks />} />
              <Route path="content/workouts" element={<WorkoutManager />} />
              <Route path="content/exercises" element={<ExerciseDatabase />} />
              <Route path="content/food" element={<FoodDatabase />} />
              <Route path="content/programmes" element={<ProgrammeManager />} />
              <Route path="content/community" element={<CommunityModeration />} />
              <Route path="content/reports" element={<ContentReports />} />
              <Route path="marketplace/products" element={<MarketplaceProducts />} />
              <Route path="marketplace/orders" element={<MarketplaceOrders />} />
              <Route path="marketplace/brands" element={<MarketplaceBrands />} />
              <Route path="marketplace/reports" element={<MarketplaceReports />} />
              <Route path="platform/health" element={<PlatformHealth />} />
              <Route path="platform/scanner" element={<ScannerDiagnostics />} />
              <Route path="platform/costs" element={<PlatformCosts />} />
              <Route path="platform/errors" element={<PlatformErrors />} />
              <Route path="platform/notifications" element={<NotificationLog />} />
              <Route path="broadcast" element={<BroadcastCompose />} />
              <Route path="broadcast/log" element={<BroadcastLog />} />
              <Route path="settings/flags" element={<FeatureFlags />} />
              <Route path="settings/costs" element={<CostThresholds />} />
              <Route path="settings/admins" element={<AdminAccounts />} />
              <Route path="settings/audit" element={<AuditLog />} />
              <Route path="settings/diagnostics" element={<ConfigDiagnostics />} />
              <Route path="arena/applications" element={<ArenaApplications />} />
              <Route path="arena/gyms" element={<ArenaGyms />} />
              <Route path="arena/passes" element={<ArenaPasses />} />
              <Route path="arena/checkins" element={<ArenaCheckinsAdmin />} />
              <Route path="arena/disputes" element={<ArenaDisputes />} />
              <Route path="arena/revenue" element={<ArenaRevenue />} />
              <Route path="*" element={<SuperadminPlaceholder />} />
            </Route>

            {/* Legal — public (no auth required) */}
            <Route path="/legal/privacy" element={<PrivacyPolicy />} />
            <Route path="/legal/terms" element={<TermsOfService />} />
            <Route path="/legal/refund" element={<RefundPolicy />} />

            {/* Marketplace */}
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/marketplace/search" element={<MarketplaceSearch />} />
            <Route path="/marketplace/product/:id" element={<ProductDetail />} />
            <Route path="/marketplace/cart" element={<ProtectedRoute><Cart /></ProtectedRoute>} />
            <Route path="/marketplace/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
            <Route path="/marketplace/order-confirmed/:id" element={<ProtectedRoute><OrderConfirmed /></ProtectedRoute>} />
            <Route path="/marketplace/sell" element={<ProtectedRoute><Sell /></ProtectedRoute>} />
            <Route path="/marketplace/sell/brand-apply" element={<BrandApply />} />
            <Route path="/marketplace/brand-apply" element={<Navigate to="/marketplace/sell/brand-apply" replace />} />
            <Route path="/sell-on-fitfrik" element={<Navigate to="/marketplace/sell/brand-apply" replace />} />
            <Route path="/marketplace/orders" element={<ProtectedRoute><MyOrders /></ProtectedRoute>} />
            <Route path="/marketplace/orders/:order_id" element={<ProtectedRoute><OrderDetail /></ProtectedRoute>} />
            <Route path="/marketplace/my-listings" element={<ProtectedRoute><MyListings /></ProtectedRoute>} />

            {/* Member My Plans (received from trainers) */}
            <Route path="/me/plans" element={<ProtectedRoute><MyPlans /></ProtectedRoute>} />

            <Route path="*" element={<Navigate to="/home" replace />} />
          </Routes>
        </HashSessionDetector>
        </BrowserRouter>
      </AppErrorBoundary>
      <Toaster position="top-center" richColors closeButton />
    </div>
  );
}

export default App;
